import { useEffect, useMemo, useState } from "react";
import { saveSeoMeta, loadSeoMeta } from "./adminApi";

const PAGE_ROUTES = [
  { label: "Default Site", route: "default" },
  { label: "Home", route: "/" },
  { label: "About", route: "/about" },
  { label: "Services", route: "/expertise" },
  { label: "Innovation", route: "/innovation" },
  { label: "Activities", route: "/activities" },
  { label: "Blog", route: "/blog" },
  { label: "Contact", route: "/contact" },
  { label: "Gallery", route: "/gallery" },
  { label: "Published", route: "/published" },
  { label: "Waiting", route: "/waiting" },
  { label: "Wishlist", route: "/wishlist" },
  { label: "Cart", route: "/cart" },
  { label: "Team", route: "/team" },
  { label: "Book", route: "/book" },
];

function createDefaultState() {
  return PAGE_ROUTES.reduce((state, page) => {
    state[page.route] = { title: "", description: "", keywords: "" };
    return state;
  }, {});
}

function AdminSeoMeta() {
  const [selectedRoute, setSelectedRoute] = useState("default");
  const [metaState, setMetaState] = useState(createDefaultState());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    async function fetchSeoMeta() {
      try {
        const response = await loadSeoMeta();
        const nextState = createDefaultState();

        response.seo_meta.forEach((entry) => {
          const route = entry.route === "default" ? "default" : entry.route;
          nextState[route] = {
            title: entry.title || "",
            description: entry.description || "",
            keywords: entry.keywords || "",
          };
        });

        setMetaState(nextState);
      } catch (err) {
        setError(err.message || "Unable to load SEO metadata.");
      }
    }

    fetchSeoMeta();
  }, []);

  const currentMeta = metaState[selectedRoute] || { title: "", description: "", keywords: "" };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setMetaState((prev) => ({
      ...prev,
      [selectedRoute]: {
        ...prev[selectedRoute],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setIsSaving(true);

    try {
      await saveSeoMeta({
        route: selectedRoute,
        ...currentMeta,
      });
      setMessage("SEO metadata saved successfully.");
    } catch (err) {
      setError(err.message || "Unable to save SEO metadata.");
    } finally {
      setIsSaving(false);
    }
  };

  const routeOptions = PAGE_ROUTES.map((page) => (
    <option key={page.route} value={page.route}>
      {page.label}
    </option>
  ));

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="eyebrow">SEO Metadata</p>
          <h2>Page Meta Settings</h2>
          <p className="admin-page-note">
            Add titles, descriptions, and keyword tags for each page. Defaults are used when a page entry is blank.
          </p>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {message && <p className="admin-success-message">{message}</p>}

      <form className="admin-form" onSubmit={handleSubmit} noValidate>
        <div className="panel-block">
          <label htmlFor="route">Select Page</label>
          <select
            id="route"
            value={selectedRoute}
            onChange={(event) => setSelectedRoute(event.target.value)}
          >
            {routeOptions}
          </select>
        </div>

        <div className="panel-block">
          <label htmlFor="metaTitle">Meta Title</label>
          <input
            id="metaTitle"
            type="text"
            value={currentMeta.title}
            onChange={handleChange("title")}
            placeholder="Enter page title"
          />
        </div>

        <div className="panel-block">
          <label htmlFor="metaDescription">Meta Description</label>
          <textarea
            id="metaDescription"
            rows={4}
            value={currentMeta.description}
            onChange={handleChange("description")}
            placeholder="Enter page description"
          />
        </div>

        <div className="panel-block">
          <label htmlFor="metaKeywords">Meta Keywords</label>
          <input
            id="metaKeywords"
            type="text"
            value={currentMeta.keywords}
            onChange={handleChange("keywords")}
            placeholder="keyword1, keyword2, keyword3"
          />
        </div>

        <div className="add-blog-actions">
          <button type="submit" className="admin-button primary" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Meta"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminSeoMeta;
